/* eslint-disable no-restricted-imports */
import { TrackingEventTypes } from '@eagle/data-function-types';
import { ArrowUp, Flag } from '@ethings/icons';
import { Theme } from '@mui/material';
import L from 'leaflet';
import { ReactNode } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { EventDefinition } from '../../../../notable-events';
import { makeStyles } from '../../../../theme';
import { Maybe, Undefinable } from '../../../../types';
import { EventLocationData } from '../../thing-event-pane/thing-event-pane.types';
import { Cluster } from './cluster';

export const POINT_RADIUS = 17.0;
export const ENLARGED_POINT_RADIUS = POINT_RADIUS * 1.2;
export const BUBBLE_RADIUS_MIN = 40;
export const BUBBLE_RADIUS_MAX = 60;
export const BUBBLE_LARGEST_RADIUS_COUNT = 300;
export const BOUNDING_RADIUS = 30.0;
const START_END_SCALE = 1.2;

export interface NotableEventDataItem {
  enabled: boolean;
  eventData: EventLocationData;
  index: number;
  priority: number;
}

interface NotableEventInfo {
  eventData: EventLocationData;
  index: number;
}

interface NotableEvents {
  containsFirstEvent: boolean;
  containsLastEvent: boolean;
  notableEventData: NotableEventDataItem[];
  firstJourneyEvent: Undefinable<NotableEventInfo>;
  lastJourneyEvent: Undefinable<NotableEventInfo>;
}

export const getMarkerSize = (eventId: string, hoveredEventId: Maybe<string>): number => {
  return eventId === hoveredEventId ? ENLARGED_POINT_RADIUS : POINT_RADIUS;
};

export const getClusterMarkerSize = (eventIds: string[], hoveredEventId: Maybe<string>, defaultRadius?: number): number => {
  const clusterRadius = defaultRadius ?? POINT_RADIUS;
  if (hoveredEventId && eventIds.includes(hoveredEventId)) return clusterRadius * START_END_SCALE;
  return clusterRadius;
};

export const getNotableEventsInformation = (items: NotableEventInfo[], eventsLength: number, notableEvents: EventDefinition[]): NotableEvents => {
  const locationUpdateEvents = items.filter((item) =>
    item.eventData.eventTypeId === TrackingEventTypes.LOCATION_UPDATE,
  );

  const updatedEvents = [
    ...items.filter((item) => item.eventData.eventTypeId !== TrackingEventTypes.LOCATION_UPDATE),
    ...locationUpdateEvents,
  ];

  const notableEventData = updatedEvents.map((item) => {
    const dictionarySearch = (notableEvents.find((event) => event.eventTypeId === item.eventData.eventTypeId) ?? notableEvents[0]);
    return {
      ...item,
      enabled: dictionarySearch.enabled,
      priority: dictionarySearch.priority,
    };
  }).filter((item) => item.enabled);

  const containsFirstEvent = !!items.find((eventItem) => eventItem.index === 0);
  const containsLastEvent = !!items.find((eventItem) => eventItem.index === (eventsLength - 1));
  const firstJourneyEvent = items.find((eventItem) => eventItem.index === 0);
  const lastJourneyEvent = items.find((eventItem) => eventItem.index === (eventsLength - 1));

  return {
    containsFirstEvent,
    containsLastEvent,
    notableEventData,
    firstJourneyEvent,
    lastJourneyEvent,
  };
};

export const createClusterMarker = (
  color: string,
  size: number,
  firstMarker: boolean,
  lastMarker: boolean,
  isOngoing: boolean,
  iconColor: string,
  locationUpdate: boolean,
  theme: Theme,
  heading?: number,
): L.DivIcon => {
  const overwriteIconColor = !locationUpdate ? color : iconColor;

  const styles = makeStyles()(() => ({
    icon: {
      backgroundColor: (firstMarker || lastMarker) ? overwriteIconColor : color,
      borderRadius: '50%',
      boxShadow: theme.shadows[4],
      '& .MuiSvgIcon-root': {
        height: '100%',
        width: '100%',
      },
      zIndex: 1000,
    },
  }))();

  if (firstMarker) {
    return L.divIcon({
      className: '',
      html: renderToStaticMarkup(
        <div
          style={{
            alignItems: 'center',
            backgroundColor: iconColor,
            borderRadius: '50%',
            boxShadow: theme.shadows[4],
            display: 'flex',
            height: '100%',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <ArrowUp
            style={{
              color: theme.palette.common.white,
              height: '100%',
              transform: `rotate(${heading ?? 0}deg)`,
              width: '100%',
              padding: theme.spacing(0.5),
            }}
            data-testid="arrowUp-icon"
          />
        </div>,
      ),
      iconSize: [size * START_END_SCALE, size * START_END_SCALE],
    });
  }

  if (lastMarker) {
    if (isOngoing) {
      const pulseColor = theme.palette.success.light;
      const pulseShadowSize = 10;
      return L.divIcon({
        className: '',
        html: [
          '<div style="position:relative;width:16px;height:16px;">',
          '<style>@keyframes trip-history-ongoing-pulse{0%{transform:scale(0.9);box-shadow:0 0 0 0 ',
          pulseColor,
          ';}70%{transform:scale(1);box-shadow:0 0 0 ',
          pulseShadowSize,
          'px ',
          `${pulseColor}00`,
          ';}100%{transform:scale(0.9);box-shadow:0 0 0 0 ',
          `${pulseColor}00`,
          ';}}</style>',
          '<div style="width:16px;height:16px;border-radius:50%;background:',
          pulseColor,
          ';box-shadow:0 0 0 0 ',
          pulseColor,
          ';animation:trip-history-ongoing-pulse 2000ms infinite;"></div>',
          '</div>',
        ].join(''),
        iconSize: [16, 16],
      });
    }

    return L.divIcon({
      className: styles.classes.icon,
      html: renderToStaticMarkup(<Flag data-testid="flag-icon" style={{ color: theme.palette.common.white, padding: theme.spacing(0.5) }} />),
      iconSize: [size * START_END_SCALE, size * START_END_SCALE],
    });
  }

  return L.divIcon({ className: styles.classes.icon, iconSize: [size, size] });
};

export const createBubbleClusterMarker = (size = 32, color?: string, textColor?: string, content?: ReactNode): L.DivIcon => {
  return L.divIcon({
    html: renderToStaticMarkup(<Cluster size={size} color={color} content={content} textColor={textColor} />),
    iconSize: [size, size],
  });
};

export const getClusterBubbleMarkerSize = (
  eventLength: number,
): number => {
  const minRadius = BUBBLE_RADIUS_MIN;
  const maxRadius = BUBBLE_RADIUS_MAX;
  const maxCount = BUBBLE_LARGEST_RADIUS_COUNT;
  const count = Math.max(1, eventLength);
  const scale = Math.min(count, maxCount) / maxCount;
  return minRadius + (maxRadius - minRadius) * scale;
};

export const getClusterTooltipOffset = (markerSize: number): number => {
  const BASE_OFFSET = 20;
  const MAX_OFFSET = 30;
  if (markerSize <= BUBBLE_RADIUS_MIN) return BASE_OFFSET;
  if (markerSize >= BUBBLE_RADIUS_MAX) return MAX_OFFSET;
  const scale = (markerSize - BUBBLE_RADIUS_MIN) / (BUBBLE_RADIUS_MAX - BUBBLE_RADIUS_MIN);
  return BASE_OFFSET + (MAX_OFFSET - BASE_OFFSET) * scale;
};

export const createBoundingMarker = (size: number): L.DivIcon => {
  const styles = makeStyles()(() => ({
    icon: {
      backgroundColor: 'transparent',
      borderRadius: '50%',
    },
  }))();

  return L.divIcon({ className: styles.classes.icon, iconSize: [size, size] });
};
